import type { AxiosResponse } from "axios"
import platform from "platform"
import { clean_params, parse_unix_date, parseUtcDate } from "@/core/helpers/utils"
import type {
  CreateManagedAgentPayload,
  CreateInteractiveOAuthClientPayload,
  CreatedManagedAgent,
  CreatedInteractiveOAuthClient,
  CreatedServiceIdentity,
  CreateStaticTokenPayload,
  CreatedStaticToken,
  OAuthAgent,
  OAuthClient,
  OAuthGrant
} from "@/types/oauth"
import type { ObjectStorageAccessPolicy } from "@/types/access-policy"

type Nullable<T> = T | null

export type LoginOptions = {
  google_id_token?: string
  device_id?: string
  mfa_code?: string
  mfa_backup_code?: string
}

export type LoginResult = string

export type LoginMfaRequiredResult = {
  mfa_required: boolean
}

export type LoginMfaCooldownResult = {
  seconds_to_wait: number
  reason?: string
}

export type AuthenticateResult = {
  id?: number
  client_space_id?: number
  company_id: number
  reseller_id?: number
  roles: string[]
}

export type MfaStatusResult = {
  has_2fa: boolean
  verified?: Nullable<boolean>
}

export type MfaSetupResult = {
  url: string
  recovery_codes: string[]
}

export type MfaAppUrlResult = {
  url: string
}

export type MfaRecoveryCodeStatusResult = {
  code_count: number
}

export type MfaRecoveryCodesResult = {
  new_codes: string[]
}

export type Session = {
  id: string
  user_id?: number
  token: string
  issue_timestamp: string
  expiry_timestamp: string
  last_used: string
  device_id?: string
  client_os?: string
  client_name?: string
  client_version?: string
  geo_country?: string
  geo_city?: string
  is_current?: boolean
}

export type PasswordResetValidateResult = {
  valid: boolean
}

export type ListBatchSessionsParams = {
  ids?: number[] | string
  company_id?: number
}

export type RolesOfUsersParams = {
  ids?: number[] | string
  company_id?: number
}

export type ChangePasswordPayload = {
  current_password: string
  new_password: string
  kill_sessions?: boolean
}

export type AdminChangePasswordPayload = {
  user_id: number
  new_password: string
  kill_sessions?: boolean
  message?: string
}

export type OAuthClientIdentity = {
  name: string
  logo_uri?: string
  // TODO add verified status
}

type OAuthGrantResponse = Omit<OAuthGrant, "issued_at" | "expires_at" | "revoked_at"> & {
  issued_at: string
  expires_at: string | null
  revoked_at: string | null
}

type OAuthAgentResponse = Omit<OAuthAgent, "created_at" | "disabled_at"> & {
  created_at: number
  disabled_at: number
}

export type OAuthAuthorizationRequest = {
  responseType: string;
  clientId: string;
  redirectUri: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  resource?: string;
  state?: string;
  requestedScope?: string;
};


export type OAuthAuthorizationDecision = "allow" | "deny"

export type OAuthAuthorizationPayload = {
  decision: OAuthAuthorizationDecision
  oauthRequest: OAuthAuthorizationRequest
  authorization: ObjectStorageAccessPolicy
}

/**
 * @description service to call HTTP request via Axios
 */
class AuthService {
    /**
     * @description Service base URL (without trailing `/`)
     */
    public static base_url: string = import.meta.env.VITE_APP_AUTH_SERVICE_URL

    public static axios: any

    public static init (axiosInstance: any) {
      AuthService.axios = axiosInstance
    }

    private static ids_param(ids?: number[] | string): string | undefined {
      return Array.isArray(ids) ? ids.join(",") : ids
    }

    private static form_data(payload: Record<string, unknown>): FormData {
      const formData = new FormData()
      Object.entries(clean_params(payload)).forEach(([key, value]) => {
        formData.append(key, String(value))
      })
      return formData
    }

    private static parse_oauth_grants(response: AxiosResponse<OAuthGrantResponse[]>): AxiosResponse<OAuthGrant[]> {
      const parsedResponse = response as unknown as AxiosResponse<OAuthGrant[]>
      parsedResponse.data = response.data.map(grant => ({
        ...grant,
        issued_at: parseUtcDate(grant.issued_at),
        expires_at: grant.expires_at == null ? null : parseUtcDate(grant.expires_at),
        revoked_at: grant.revoked_at == null ? null : parseUtcDate(grant.revoked_at)
      }))
      return parsedResponse
    }

    public static login(email: string, pass: string, options: LoginOptions = {}): Promise<AxiosResponse<LoginResult>> {
      const payload = AuthService.form_data({
        email_address: email,
        password: pass,
        google_id_token: options.google_id_token,
        device_id: options.device_id,
        client_os: platform.os?.family,
        client_name: platform.name,
        client_version: platform.version,
        mfa_code: options.mfa_code,
        mfa_backup_code: options.mfa_backup_code
      })

      return AuthService.axios.post(`${AuthService.base_url}/login`, payload)
    }

    public static logout(token?: string): Promise<AxiosResponse<void>> {
      const config = token ? {headers: {Authorization: `Bearer ${token}`}} : undefined
      return AuthService.axios.get(`${AuthService.base_url}/logout`, config)
    }

    public static verify_token(token: string): Promise<AxiosResponse<AuthenticateResult>> {
      return AuthService.axios.get(`${AuthService.base_url}/authenticate`, {headers: {Authorization: `Bearer ${token}`}})
    }

    public static authenticate(token: string): Promise<AxiosResponse<AuthenticateResult>> {
      return AuthService.axios.get(`${AuthService.base_url}/authenticate`, { params: { token } })
    }

    public static resend_confirmation_email(user_id: number): Promise<AxiosResponse<void>> {
      return AuthService.axios.post(`${AuthService.base_url}/resend_confirmation_email`, { user_id })
    }

    public static confirm_user_account(confirm_key: string, user_id?: number): Promise<AxiosResponse<void>> {
      return AuthService.axios.get(`${AuthService.base_url}/confirm_registration`, {
        params: clean_params({ user_id, challenge: confirm_key })
      })
    }

    public static get_2fa_status(): Promise<AxiosResponse<MfaStatusResult>> {
      return AuthService.axios.get(`${AuthService.base_url}/2fa/status`)
    }

    public static setup_2fa(): Promise<AxiosResponse<MfaSetupResult>> {
      return AuthService.axios.post(`${AuthService.base_url}/2fa/setup`)
    }

    public static delete_2fa(password: string): Promise<AxiosResponse<boolean>> {
      return AuthService.axios.post(`${AuthService.base_url}/2fa/delete`, { password })
    }

    public static get_2fa_app_url(): Promise<AxiosResponse<MfaAppUrlResult>> {
      return AuthService.axios.get(`${AuthService.base_url}/2fa/get_app_url`)
    }

    public static verify_2fa_code(code: string): Promise<AxiosResponse<boolean>> {
      return AuthService.axios.post(`${AuthService.base_url}/2fa/verify_code`, { code })
    }

    public static get_2fa_recovery_code_status(): Promise<AxiosResponse<MfaRecoveryCodeStatusResult>> {
      return AuthService.axios.get(`${AuthService.base_url}/2fa/recovery_code_status`)
    }

    public static get_2fa_recovery_codes(): Promise<AxiosResponse<MfaRecoveryCodeStatusResult>> {
      return AuthService.axios.get(`${AuthService.base_url}/2fa/get_recovery_codes`)
    }

    public static generate_2fa_recovery_codes(): Promise<AxiosResponse<MfaRecoveryCodesResult>> {
      return AuthService.axios.post(`${AuthService.base_url}/2fa/generate_recovery_codes`)
    }

    public static generate_2fa_recovery_codes_legacy(): Promise<AxiosResponse<MfaRecoveryCodesResult>> {
      return AuthService.axios.get(`${AuthService.base_url}/2fa/generate_recovery_codes`)
    }

    public static list_sessions(): Promise<AxiosResponse<Session[]>> {
      return AuthService.axios.get(`${AuthService.base_url}/session/list`)
    }

    public static list_sessions_batch(params: ListBatchSessionsParams = {}): Promise<AxiosResponse<Session[]>> {
      return AuthService.axios.get(`${AuthService.base_url}/session/list_batch`, {
        params: clean_params({
          ids: AuthService.ids_param(params.ids),
          company_id: params.company_id
        })
      })
    }

    public static invalidate_session(session_id: string): Promise<AxiosResponse<void>> {
      return AuthService.axios.put(`${AuthService.base_url}/session/invalidate`, AuthService.form_data({ session_id }))
    }

    public static change_password(payload: ChangePasswordPayload): Promise<AxiosResponse<void>> {
      return AuthService.axios.post(`${AuthService.base_url}/change_password`, AuthService.form_data(payload))
    }

    public static change_password_admin(payload: AdminChangePasswordPayload): Promise<AxiosResponse<void>> {
      return AuthService.axios.post(`${AuthService.base_url}/change_password/admin`, AuthService.form_data(payload))
    }

    public static request_password_reset(email_address: string): Promise<AxiosResponse<void>> {
      return AuthService.axios.post(`${AuthService.base_url}/password_reset/request`, { email_address })
    }

    public static validate_password_reset(token: string): Promise<AxiosResponse<PasswordResetValidateResult>> {
      return AuthService.axios.post(`${AuthService.base_url}/password_reset/validate`, { token })
    }

    public static confirm_password_reset(token: string, new_password: string): Promise<AxiosResponse<void>> {
      return AuthService.axios.post(`${AuthService.base_url}/password_reset/confirm`, { token, new_password })
    }

    // Pass a user ID when a team admin deletes another team member. Do not pass anything to delete the
    // logged-in user's own account
    public static delete_user(user_id?: number): Promise<AxiosResponse<void>> {
      return AuthService.axios.delete(`${AuthService.base_url}/user`, {
        params: clean_params({ user_id })
      })
    }

    public static make_user_admin(user_id: number): Promise<AxiosResponse<void>> {
      return AuthService.axios.post(`${AuthService.base_url}/grant_role`, AuthService.form_data({ user_id, role: "OA" }))
    }

    public static remove_user_admin(user_id: number): Promise<AxiosResponse<void>> {
      return AuthService.axios.delete(`${AuthService.base_url}/revoke_role`, {
        params: { user_id, role: "OA" }
      })
    }

    public static list_oauth_grants(): Promise<AxiosResponse<OAuthGrant[]>> {
      return AuthService.axios.get(`${AuthService.base_url}/oauth/grants`)
        .then(AuthService.parse_oauth_grants)
    }

    public static list_oauth_clients(): Promise<AxiosResponse<OAuthClient[]>> {
      return AuthService.axios.get(`${AuthService.base_url}/oauth/clients`)
    }

    public static list_oauth_agents(): Promise<AxiosResponse<OAuthAgent[]>> {
      return AuthService.axios.get(`${AuthService.base_url}/oauth/agents`)
        .then((response: AxiosResponse<OAuthAgentResponse[]>) => {
          const parsedResponse = response as unknown as AxiosResponse<OAuthAgent[]>
          parsedResponse.data = response.data.map(agent => ({
            ...agent,
            created_at: parse_unix_date(agent.created_at),
            disabled_at: agent.disabled_at === 0 ? null : parse_unix_date(agent.disabled_at)
          }))
          return parsedResponse
        })
    }

    public static list_static_tokens(): Promise<AxiosResponse<OAuthGrant[]>> {
      return AuthService.axios.get(`${AuthService.base_url}/oauth/static-tokens`)
        .then(AuthService.parse_oauth_grants)
    }

    public static create_oauth_client(
      client_name: string,
      authorization: ObjectStorageAccessPolicy,
      client_description?: string,
      logo_uri?: string
    ): Promise<AxiosResponse<CreatedServiceIdentity>> {
      return AuthService.axios.post(`${AuthService.base_url}/oauth/clients`, {
        client_name,
        authorization,
        client_description,
        logo_uri
      })
    }

    public static provision_managed_agent(
      payload: CreateManagedAgentPayload
    ): Promise<AxiosResponse<CreatedManagedAgent>> {
      return AuthService.axios.post(`${AuthService.base_url}/oauth/managed-agents`, payload)
    }

    public static create_interactive_oauth_client(
      payload: CreateInteractiveOAuthClientPayload
    ): Promise<AxiosResponse<CreatedInteractiveOAuthClient>> {
      return AuthService.axios.post(`${AuthService.base_url}/oauth/interactive-clients`, payload)
    }

    public static create_static_token(
      payload: CreateStaticTokenPayload
    ): Promise<AxiosResponse<CreatedStaticToken>> {
      return AuthService.axios.post(`${AuthService.base_url}/oauth/static-tokens`, payload)
    }

    public static get_agent_info(clientId: string): Promise<AxiosResponse<OAuthClientIdentity>> {
      // client_id might be a URL when the client uses CIMD. Axios encodes it automatically.
      return AuthService.axios.get(`${AuthService.base_url}/oauth/authorize/agent`, {
        params: { client_id: clientId }
      })
    }

    public static submit_oauth_authorization(payload: OAuthAuthorizationPayload, bearerToken: string): void {
      const form = document.createElement("form")
      const request = payload.oauthRequest

      form.method = "post"
      form.action = `${AuthService.base_url}/oauth/authorize`
      form.enctype = "application/x-www-form-urlencoded"
      form.style.display = "none"

      appendHiddenFormValue(form, "access_token", bearerToken)
      appendHiddenFormValue(form, "decision", payload.decision)
      appendHiddenFormValue(form, "response_type", request.responseType)
      appendHiddenFormValue(form, "client_id", request.clientId)
      appendHiddenFormValue(form, "redirect_uri", request.redirectUri)
      appendHiddenFormValue(form, "code_challenge", request.codeChallenge)
      appendHiddenFormValue(form, "code_challenge_method", request.codeChallengeMethod)
      appendOptionalHiddenFormValue(form, "resource", request.resource)
      appendOptionalHiddenFormValue(form, "state", request.state)
      appendHiddenFormValue(form, "authorization", JSON.stringify({
        permissions: payload.authorization.permissions,
        conditions: payload.authorization.conditions,
        purpose: payload.authorization.purpose
      }))

      document.body.appendChild(form)
      form.submit()
    }
}

function appendHiddenFormValue(form: HTMLFormElement, key: string, value: string) {
  const input = document.createElement("input")
  input.type = "hidden"
  input.name = key
  input.value = value
  form.appendChild(input)
}

function appendOptionalHiddenFormValue(form: HTMLFormElement, key: string, value: string | undefined) {
  if (value) {
    appendHiddenFormValue(form, key, value)
  }
}

export default AuthService
